import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Save, ExternalLink } from "lucide-react";
import JsBarcode from "jsbarcode";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const CARDS = [
  { key: "everyday_rewards", label: "Everyday Rewards", shop: "Woolies", color: "#3E8E41", app: "https://www.everyday.com.au/rewards", hint: "13 digits, starts 9355" },
  { key: "flybuys", label: "Flybuys", shop: "Coles", color: "#1E5AA8", app: "https://www.flybuys.com.au/", hint: "16 digits, starts 6008" },
];

export function Barcode({ value, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      const format = value.length === 13 ? "EAN13" : "CODE128";
      JsBarcode(ref.current, value, { format, displayValue: true, fontSize: 14, height: 70, margin: 8, background: "#ffffff", lineColor: "#2B2438" });
    } catch { JsBarcode(ref.current, value, { format: "CODE128", displayValue: true, height: 70, margin: 8 }); }
  }, [value]);
  return <svg ref={ref} className="w-full" data-testid={testId} />;
}

export default function RewardsWallet() {
  const { user, refreshUser } = useAuth();
  const [vals, setVals] = useState({ everyday_rewards: "", flybuys: "" });
  const [show, setShow] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user?.rewards) setVals({ everyday_rewards: user.rewards.everyday_rewards || "", flybuys: user.rewards.flybuys || "" }); }, [user]);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/rewards", vals); setVals(data); await refreshUser?.(); toast.success("Rewards cards saved — they'll pop up at checkout"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft p-4" data-testid="rewards-wallet">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-[#DDEBE1] flex items-center justify-center text-emerald-600"><CreditCard size={18} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink">Rewards wallet</p>
          <p className="text-xs text-sorted-slate">Scan at the checkout or in store — no more digging for the card.</p>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {CARDS.map((c) => (
          <div key={c.key} className="rounded-2xl border-2 border-[#FFF3F0] p-3" data-testid={`rewards-${c.key}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold" style={{ color: c.color }}>{c.label} <span className="text-sorted-slate font-semibold">· {c.shop}</span></p>
              <a href={c.app} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-sorted-slate flex items-center gap-1" data-testid={`rewards-app-${c.key}`}>Open app <ExternalLink size={11} strokeWidth={2.5} /></a>
            </div>
            <input inputMode="numeric" data-testid={`rewards-input-${c.key}`} value={vals[c.key]} onChange={(e) => setVals({ ...vals, [c.key]: e.target.value })}
              placeholder={c.hint} className="mt-2 w-full bg-muted rounded-2xl px-3 py-2.5 text-sm font-bold tracking-wider text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach" />
            {vals[c.key]?.replace(/\D/g, "").length >= 12 && (
              <button onClick={() => setShow(show === c.key ? null : c.key)} data-testid={`rewards-show-${c.key}`} className="mt-2 text-xs font-bold text-sorted-rose">{show === c.key ? "Hide barcode" : "Show barcode"}</button>
            )}
            {show === c.key && <div className="mt-2 bg-white rounded-2xl border border-[#FFF3F0] p-1"><Barcode value={vals[c.key].replace(/\D/g, "")} testId={`rewards-barcode-${c.key}`} /></div>}
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="rewards-save" className="mt-4 w-full bg-sorted-peach text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-2"><Save size={16} strokeWidth={2.5} /> Save cards</button>
      <p className="text-[11px] text-sorted-slate mt-2">Points are earned in the Woolies / Coles checkout — Millie gets your trolley there and shows your card. A live points link needs a partner agreement with the supermarkets, so it's not on yet.</p>
    </div>
  );
}
